import axios from 'axios';
import { config } from '../config';

// Base URL for our backend API
// This points to our Node.js server running on port 3001
const API_URL = config.apiUrl;

/**
 * TypeScript Interface: Defines the structure of login response data
 * This ensures type safety - we know exactly what data to expect
 */
interface LoginResponse {
  token: string;    // JWT token for authentication
  user: {
    id: number;
    email: string;
    name: string;
  };
  isNewUser?: boolean;  // Indicates if this is a new user signup (for OAuth)
}

/**
 * TypeScript Interface: Defines the structure of signup response data
 */
interface SignupResponse {
  message: string;
  user: {
    id: number;
    email: string;
    name: string;
  };
}

/**
 * AuthService Class - Handles all authentication-related operations
 * 
 * This service manages:
 * - User login/signup
 * - Social authentication (Google, Apple)
 * - Token storage and management
 * - Authentication state
 * - Password reset functionality
 */
class AuthService {
  // Private property: Stores the current authentication token
  private token: string | null = null;
  private authListeners: Array<(isAuthenticated: boolean) => void> = [];

  /**
   * Constructor: Runs when a new AuthService instance is created
   * Attempts to restore authentication token from browser storage
   */
  constructor() {
    // Try to get existing token from localStorage (persists between browser sessions)
    this.token = localStorage.getItem('token');
  }

  /**
   * Add listener for authentication state changes
   */
  addAuthListener(callback: (isAuthenticated: boolean) => void): void {
    this.authListeners.push(callback);
  }

  /**
   * Remove listener for authentication state changes
   */
  removeAuthListener(callback: (isAuthenticated: boolean) => void): void {
    this.authListeners = this.authListeners.filter(listener => listener !== callback);
  }

  /**
   * Notify all listeners of authentication state change
   */
  private notifyAuthChange(): void {
    const isAuth = this.isAuthenticated();
    this.authListeners.forEach(callback => callback(isAuth));
  }

  /**
   * Login Method - Authenticates user with email/password
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await axios.post(`${API_URL}/auth/signin`, { email, password });
      const { token, user } = response.data;
      this.setToken(token);
      return { token, user };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Signup Method - Creates new user account with just name and email
   * Sends welcome email with password setup link
   */
  async signup(email: string, name: string): Promise<SignupResponse> {
    try {
      const response = await axios.post(`${API_URL}/auth/signup`, { email, name });
      return response.data;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  /**
   * Forgot Password Method - Sends password reset email
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      return response.data;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  /**
   * Set Password Method - Sets password using token from email
   * Returns session token and user info for auto sign-in
   */
  async setPassword(token: string, password: string): Promise<{ message: string; token?: string; user?: any }> {
    try {
      const response = await axios.post(`${API_URL}/auth/set-password`, { token, password });
      
      // If we get a session token back, store it for auto sign-in
      if (response.data.token) {
        this.setToken(response.data.token);
      }
      
      return response.data;
    } catch (error) {
      console.error('Set password error:', error);
      throw error;
    }
  }

  /**
   * Google Sign-In Method - Handles OAuth authentication with Google
   */
  async signInWithGoogle(): Promise<LoginResponse> {
    return new Promise(async (resolve, reject) => {
      let timeoutId: NodeJS.Timeout | null = null;
      
      try {
        // Step 1: Dynamically load Google's OAuth JavaScript library if not already loaded
        if (!window.google?.accounts) {
          await new Promise<void>((loadResolve, loadReject) => {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => loadResolve();
            script.onerror = loadReject;
            document.head.appendChild(script);
          });
        }

        // Step 2: Initialize Google OAuth client with FedCM support
        // Initialize only once to avoid re-initialization issues
        if (!window.googleSignInInitialized) {
          google.accounts.id.initialize({
            client_id: config.googleClientId,
            callback: async (response: google.accounts.id.CredentialResponse) => {
              // Clear the timeout since we got a response
              if (window.googleSignInTimeoutId) {
                clearTimeout(window.googleSignInTimeoutId);
                window.googleSignInTimeoutId = null;
              }
              
              try {
                // Step 3: Send Google's ID token to our backend for verification
                const result = await axios.post(`${API_URL}/auth/google`, {
                  token: response.credential,
                });
                const { token, user } = result.data;
                this.setToken(token);
                
                // Resolve any pending promise
                if (window.googleSignInResolve) {
                  window.googleSignInResolve({ token, user });
                  window.googleSignInResolve = null;
                  window.googleSignInReject = null;
                }
              } catch (error) {
                if (window.googleSignInReject) {
                  window.googleSignInReject(error);
                  window.googleSignInResolve = null;
                  window.googleSignInReject = null;
                }
              }
            },
            auto_select: false,
            cancel_on_tap_outside: false,
            use_fedcm_for_prompt: true
          });
          window.googleSignInInitialized = true;
        }

        // Store resolve/reject for the callback to use
        window.googleSignInResolve = resolve;
        window.googleSignInReject = reject;

        // Step 4: Trigger the Google Sign-In prompt without moment listener
        google.accounts.id.prompt((n) => {
          if (n.isNotDisplayed() || n.isSkippedMoment()) {
            reject(new Error('User not signed in to Google'));
          }
        });
        
        // Set a timeout in case the prompt doesn't show or user cancels
        // Store the timeout ID globally so the callback can clear it
        timeoutId = setTimeout(() => {
          if (window.googleSignInReject) {
            window.googleSignInReject(new Error('Sign-in timeout or cancelled'));
            window.googleSignInResolve = null;
            window.googleSignInReject = null;
            window.googleSignInTimeoutId = null;
          }
        }, 60000); // Increase to 60 second timeout for slower responses
        
        window.googleSignInTimeoutId = timeoutId;
        
      } catch (error) {
        // Clear timeout if error occurs
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        console.error('Google sign in error:', error);
        reject(error);
      }
    });
  }

  /**
   * Apple Sign-In Method - Handles Apple ID authentication
   */
  async signInWithApple(): Promise<LoginResponse> {
    try {
      // Check if Apple Sign-In is available
      if (!window.AppleID) {
        throw new Error('Apple Sign-In is not available');
      }

      // Initialize Apple Sign-In
      await window.AppleID.auth.init({
        clientId: config.appleClientId,
        scope: 'name email',
        redirectURI: config.appleRedirectUri,
        state: 'apple-signin',
        usePopup: true
      });

      // Perform Apple Sign-In
      const appleResponse = await window.AppleID.auth.signIn();
      
      if (!appleResponse.authorization) {
        throw new Error('Apple Sign-In was cancelled');
      }

      // Send Apple's authorization code to our backend
      const result = await axios.post(`${API_URL}/auth/apple`, {
        code: appleResponse.authorization.code,
        id_token: appleResponse.authorization.id_token,
        user: appleResponse.user // Contains name and email on first sign-in
      });

      const { token, user } = result.data;
      this.setToken(token);
      return { token, user };
    } catch (error) {
      console.error('Apple sign in error:', error);
      throw error;
    }
  }

  /**
   * Logout Method - Clears authentication state and invalidates server session
   */
  async logout(): Promise<void> {
    try {
      if (this.token) {
        await axios.post(`${API_URL}/auth/signout`, {}, {
          headers: { Authorization: `Bearer ${this.token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.token = null;
      localStorage.removeItem('token');
      this.notifyAuthChange();
    }
  }

  /**
   * Refresh Token Method - Reissues a fresh session token before the current one expires.
   * Returns true on success. Keeps an open client signed in without re-authenticating.
   */
  async refreshToken(): Promise<boolean> {
    try {
      if (!this.token) {
        return false;
      }
      const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${this.token}` }
      });
      if (response.data?.token) {
        this.setToken(response.data.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  /**
   * Get Current User Method - Fetches current user info from server
   */
  async getCurrentUser(): Promise<any> {
    try {
      if (!this.token) {
        return null;
      }

      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${this.token}` }
      });
      return response.data.user;
    } catch (error) {
      console.error('Get current user error:', error);
      this.logout(); // Clear invalid token
      return null;
    }
  }

  /**
   * Get Token Method - Returns current authentication token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Set Token Method - Stores authentication token
   */
  private setToken(token: string): void {
    this.token = token;
    localStorage.setItem('token', token);
    this.notifyAuthChange();
  }

  /**
   * Decode JWT token to get payload
   */
  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  /**
   * Check if JWT token is expired
   */
  isTokenExpired(token?: string): boolean {
    const tokenToCheck = token || this.token;
    if (!tokenToCheck) {
      return true; // No token = expired
    }

    const payload = this.decodeJWT(tokenToCheck);
    if (!payload || !payload.exp) {
      return true; // Invalid token = expired
    }

    // exp is in seconds, Date.now() is in milliseconds
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();

    return currentTime >= expirationTime;
  }

  /**
   * Get token expiration time in milliseconds
   */
  getTokenExpirationTime(): number | null {
    if (!this.token) {
      return null;
    }

    const payload = this.decodeJWT(this.token);
    if (!payload || !payload.exp) {
      return null;
    }

    return payload.exp * 1000; // Convert to milliseconds
  }

  /**
   * Is Authenticated Method - Checks if user is currently authenticated
   * Now also validates that the token hasn't expired
   */
  isAuthenticated(): boolean {
    if (!this.token) {
      return false;
    }

    // Check if token is expired
    if (this.isTokenExpired(this.token)) {
      console.warn('Token expired, clearing authentication');
      this.logout();
      return false;
    }

    return true;
  }

  /**
   * Update Profile Settings Method - Updates user profile settings
   */
  async updateProfileSettings(settings: {
    username?: string;
    about_me?: string;
    dont_show_scripts?: boolean;
    dont_show_favorites?: boolean;
    dont_show_comments?: boolean;
  }): Promise<any> {
    try {
      if (!this.token) {
        throw new Error('Authentication required');
      }

      const response = await axios.put(`${API_URL}/user/profile-settings`, settings, {
        headers: { Authorization: `Bearer ${this.token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Update profile settings error:', error);
      throw error;
    }
  }

  /**
   * Check Username Availability Method - Checks if a username is available
   */
  async checkUsernameAvailability(username: string): Promise<boolean> {
    try {
      if (!this.token) {
        throw new Error('Authentication required');
      }

      const response = await axios.get(`${API_URL}/user/check-username/${username}`, {
        headers: { Authorization: `Bearer ${this.token}` }
      });

      return response.data.available;
    } catch (error) {
      console.error('Check username availability error:', error);
      throw error;
    }
  }

  /**
   * Regenerate Profile Image Method - Generates a new profile image
   */
  async regenerateProfileImage(prompt: string): Promise<{
    profile_image: string;
    coins_spent: number;
    remaining_coins: number;
  }> {
    try {
      if (!this.token) {
        throw new Error('Authentication required');
      }

      const response = await axios.post(`${API_URL}/user/regenerate-avatar`, { prompt }, {
        headers: { Authorization: `Bearer ${this.token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Regenerate profile image error:', error);
      throw error;
    }
  }

  /**
   * Get Meditation for Display - Gets meditation data for public/private display
   */
  async getMeditationForDisplay(id: string): Promise<{
    script_name: string;
    script_description: string;
    artUrl: string;
    final_audio_file: string;
    duration: number;
    thumbs_up: number;
    thumbs_down: number;
    views: number;
    listens: number;
    completions: number;
    auto_loop: boolean;
    is_anonymous: boolean;
    contributions: any;
    published_at: string;
    username: string;
    user_id: string;
    profile_image?: string;
    tags: string[];
    user_has_voted?: 'up' | 'down' | null;
    user_has_favorited?: boolean;
    user_is_following?: boolean;
  }> {
    try {
      const headers: any = {};
      
      // Include authorization header if we have a token (for private meditations)
      if (this.token) {
        headers.Authorization = `Bearer ${this.token}`;
      }

      const response = await axios.get(`${API_URL}/display-meditation/${id}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Get meditation for display error:', error);
      throw error;
    }
  }

  /**
   * Follow/Unfollow User - Toggles follow state for a user
   */
  async toggleFollowUser(userId: string): Promise<{ is_following: boolean }> {
    try {
      if (!this.token) {
        throw new Error('Authentication required');
      }

      const response = await axios.post(`${API_URL}/users/${userId}/follow`, {}, {
        headers: { Authorization: `Bearer ${this.token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Toggle follow user error:', error);
      throw error;
    }
  }

  /**
   * Favorite/Unfavorite Script - Toggles favorite state for a script
   */
  async toggleFavoriteScript(scriptId: string): Promise<{ is_favorited: boolean }> {
    try {
      if (!this.token) {
        throw new Error('Authentication required');
      }

      const response = await axios.post(`${API_URL}/scripts/${scriptId}/favorite`, {}, {
        headers: { Authorization: `Bearer ${this.token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Toggle favorite script error:', error);
      throw error;
    }
  }

  /**
   * Rate Script - Sets thumbs up/down rating for a script
   */
  async rateScript(scriptId: string, rating: 'like' | 'dislike'): Promise<{ 
    user_rating: 'like' | 'dislike' | null;
    thumbs_up: number;
    thumbs_down: number;
  }> {
    try {
      if (!this.token) {
        throw new Error('Authentication required');
      }

      const response = await axios.post(`${API_URL}/scripts/${scriptId}/rate`, { rating }, {
        headers: { Authorization: `Bearer ${this.token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Rate script error:', error);
      throw error;
    }
  }

  /**
   * Customize Script - Creates a customized copy of an existing script
   */
  async customizeScript(scriptId: string): Promise<{ script: any; message: string }> {
    try {
      if (!this.token) {
        throw new Error('Authentication required');
      }

      const response = await axios.post(`${API_URL}/scripts/${scriptId}/customize`, {}, {
        headers: { Authorization: `Bearer ${this.token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Customize script error:', error);
      throw error;
    }
  }

  /**
   * Track Meditation Listen - Records when a user starts listening to a meditation
   */
  async trackMeditationListen(scriptId: string, durationSeconds?: number): Promise<void> {
    try {
      const headers: any = {};
      
      // Include authorization header if we have a token (optional)
      if (this.token) {
        headers.Authorization = `Bearer ${this.token}`;
      }

      await axios.post(`${API_URL}/scripts/${scriptId}/listen`, 
        { duration_seconds: durationSeconds }, 
        { headers }
      );
    } catch (error) {
      console.error('Track listen error:', error);
      // Don't throw - tracking should not break the user experience
    }
  }

  /**
   * Track Meditation Completion - Records when a user completes a full meditation
   */
  async trackMeditationCompletion(scriptId: string): Promise<void> {
    try {
      const headers: any = {};
      
      // Include authorization header if we have a token (optional)
      if (this.token) {
        headers.Authorization = `Bearer ${this.token}`;
      }

      await axios.post(`${API_URL}/scripts/${scriptId}/complete`, {}, { headers });
    } catch (error) {
      console.error('Track completion error:', error);
      // Don't throw - tracking should not break the user experience
    }
  }
}

// Export a single instance of the AuthService
// This creates a "singleton" - one shared instance used throughout the app
export const authService = new AuthService(); 